// src/oura/components/site/CoreWebVitalsCard.tsx
//
// Core Web Vitals on the staff Site screen: PageSpeed Insights' four
// Lighthouse gauges beside the page as Lighthouse rendered it, what real
// visitors experienced, the lab test, the top opportunities and the slowest
// key pages, with a Mobile / Desktop switch and an on-demand Re-scan.

import { useState, type ReactNode } from "react";
import { ExternalLinkIcon, GaugeIcon, Loader2Icon, RefreshCwIcon } from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { syncPageSpeed } from "@service/aeo";
import type { PageSpeed, PageSpeedRun } from "../../audit";
import { discoveredDateTime, pageHref, pagePath } from "../../lib/oura";
import { cn } from "../../lib/cn";
import { Tooltip } from "../../primitives/Tooltip";
import { FOCUS_RING, FoldCard, ListRow, PillButton, Ring, SURFACE, Seg, TYPE, Tag } from "@oura/ui";
import {
    METRIC_HELP,
    RATING_TEXT,
    deltaOf,
    fieldInp,
    labDelta,
    labMetrics,
    notMeasured,
    opportunityHelp,
    psiBand,
    secs,
    slowestFirst,
    type Metric,
    type MetricDelta,
} from "./pageSpeed";

/** The card's text actions (Re-scan, Verify on PageSpeed): one style, so two
 *  controls doing equally weighted jobs never read as two button languages. */
const TEXT_ACTION = cn(
    "inline-flex items-center gap-1 whitespace-nowrap rounded font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline",
    FOCUS_RING,
);

/** The movement chip. Deliberately quiet: it sits beside the value and never
 *  competes with it, because the reading is the fact and the change is context. */
function DeltaChip({ delta }: { delta: MetricDelta | null }) {
    if (!delta) return null;
    return (
        <Tooltip content={`Change since your last re-scan. ${delta.better ? "This got better." : "This got worse."}`}>
            <span className={cn(TYPE.tm, "cursor-help whitespace-nowrap font-medium tabular-nums", delta.better ? "text-emerald-700" : "text-rose-700")}>
                {delta.text}
            </span>
        </Tooltip>
    );
}

const NOT_MEASURED_TIP = "PageSpeed Insights returned no value for this metric on this run. It is not a score of zero.";

/**
 * ONE tile shape for every statistic on this card: label, reading, one line
 * saying what the reading measures. The value carries the rating in its
 * colour, so there is no status dot saying the same thing a second time.
 */
function MetricTile({ m, delta }: { m: Metric; delta?: MetricDelta | null }) {
    const help = METRIC_HELP[m.label];
    return (
        <div className={cn(SURFACE.inner, "h-full")}>
            <p className={cn(TYPE.lab, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1")}>
                {m.label}
                {m.core && <Tag tone="teal">Core vital</Tag>}
            </p>
            <p className="m-0 mt-0.5 flex items-baseline gap-2">
                {m.value === null ? (
                    <Tooltip content={NOT_MEASURED_TIP}>
                        <span className={cn(TYPE.tb, "cursor-help font-medium text-gray-400")}>Not measured</span>
                    </Tooltip>
                ) : (
                    <>
                        <span className={cn(TYPE.tt, "tabular-nums", m.rating && RATING_TEXT[m.rating])}>{m.value}</span>
                        <DeltaChip delta={delta ?? null} />
                    </>
                )}
            </p>
            {help && <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>{help}</p>}
        </div>
    );
}

/** The Core Web Vitals Assessment, in the SAME frame as every metric tile. It
 *  is a verdict rather than a number, so the value slot carries words. */
function VerdictTile({ run }: { run: PageSpeedRun }) {
    const label = "Core Web Vitals Assessment";
    // "insufficient" is NOT a failure. Chrome has too little real-visitor data
    // for this site to judge, which is normal for a small or new site, and
    // rendering it as a red "Failed" accuses the site of something never
    // measured. It reuses the same grey treatment as an unmeasured metric.
    const insufficient = run.fieldData === "insufficient";
    return (
        <div className={cn(SURFACE.inner, "h-full")}>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            <p className="m-0 mt-0.5">
                {insufficient ? (
                    <Tooltip content="Chrome hasn't collected enough real-visitor data for this site yet, so there is no pass or fail to report. This is normal for smaller or newer sites.">
                        <span className={cn(TYPE.tb, "cursor-help font-medium text-gray-400")}>Not enough data</span>
                    </Tooltip>
                ) : (
                    <span className={cn(TYPE.tt, run.fieldPass ? "text-emerald-700" : "text-rose-700")}>
                        {run.fieldPass ? "Passed" : "Failed"}
                    </span>
                )}
            </p>
            <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>{METRIC_HELP[label]}</p>
        </div>
    );
}

/**
 * One Lighthouse category score, as a gauge.
 *
 * `score` is null when PSI returned no category. An empty ring reading "–" is
 * the honest render; the BE's old fabricated 0 drew a full red ring, a
 * catastrophic score for a site that was simply never scored. One size for
 * all four: PSI shows the categories as equals.
 */
function ScoreGauge({ score, label, delta }: { score: number | null; label: string; delta?: MetricDelta | null }) {
    const band = score === null ? null : psiBand(score);
    return (
        <div className="flex flex-col items-center text-center">
            <Ring value={score} size={76} stroke={6} color={band?.ring}>
                <span className={cn(TYPE.tt, "tabular-nums", band ? band.text : "text-gray-400")}>{score ?? "–"}</span>
            </Ring>
            <span className={cn(TYPE.tc, "mt-2 text-gray-600")}>{label}</span>
            <DeltaChip delta={delta ?? null} />
        </div>
    );
}

/** The stand-in when a run carries no screenshot: labelled as one, so it can
 *  never be mistaken for a render of a very plain site. */
function RenderPlaceholder({ mobile }: { mobile: boolean }) {
    return (
        <div aria-hidden className="flex h-full flex-col bg-gray-50">
            {!mobile && (
                <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                </div>
            )}
            <div className="flex-1 space-y-2.5 p-3.5">
                <div className="h-4 w-2/3 rounded bg-gray-200" />
                <div className="h-2.5 w-full rounded bg-gray-200" />
                <div className="h-2.5 w-5/6 rounded bg-gray-200" />
                <div className="h-14 w-full rounded bg-gray-100" />
                <div className="h-2.5 w-3/5 rounded bg-gray-200" />
            </div>
        </div>
    );
}

/**
 * The page as Lighthouse actually rendered it, at this strategy's viewport.
 *
 * `run.screenshot` is Lighthouse's `final-screenshot` audit, a base64 JPEG
 * the BE has captured since #3682. There are two honest reasons to have none
 * (the fixtures, and a PSI run that returned no screenshot audit), so the
 * placeholder stays, and its caption says what it is.
 */
function DevicePreview({ run }: { run: PageSpeedRun }) {
    const mobile = run.strategy === "mobile";
    const screen = run.screenshot ? (
        <img
            src={run.screenshot}
            // Named per device so the two renders are distinguishable to a
            // screen reader.
            alt={`${mobile ? "Mobile" : "Desktop"} render of the homepage`}
            // object-top: the fold is what matters. Centring a tall page crops
            // to its middle, which tells you nothing.
            className="h-full w-full object-cover object-top"
            loading="lazy"
        />
    ) : (
        <RenderPlaceholder mobile={mobile} />
    );
    return (
        <figure className="m-0 text-center">
            {mobile ? (
                <div className="mx-auto aspect-[9/16] w-[150px] rounded-[20px] bg-gray-900 p-1.5">
                    <div className="h-full overflow-hidden rounded-[14px] bg-white">{screen}</div>
                </div>
            ) : (
                <div className="mx-auto aspect-video w-full max-w-[520px] overflow-hidden rounded-[10px] bg-white ring-1 ring-black/[0.08]">
                    {screen}
                </div>
            )}
            <figcaption className={cn(TYPE.tm, "mt-2 text-gray-500")}>
                {run.screenshot ? `${mobile ? "Mobile" : "Desktop"} render` : "No render captured"}
            </figcaption>
        </figure>
    );
}

/** A block's label, then one sentence saying what this group of numbers is. */
function BlockHead({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
    return (
        <div className={className}>
            <p className={cn(TYPE.lab, "m-0")}>{title}</p>
            <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>{children}</p>
        </div>
    );
}

/** An empty body with its one next move, in the card's own type. */
function EmptyBody({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
    return (
        <div className="py-6 text-center">
            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{title}</p>
            <p className={cn(TYPE.tc, "m-0 mx-auto mt-1 max-w-md text-gray-500")}>{children}</p>
            {action && <div className="mt-3">{action}</div>}
        </div>
    );
}

/**
 * How many tests stand behind the lab numbers, and on what.
 *
 * The homepage run is the median of `samples` PSI calls (#4492), so "a single
 * test run just now" was wrong twice: it is the middle of three, and it ran
 * when the run was captured, not now. Older payloads do not say how many.
 */
function labLine(run: PageSpeedRun): string {
    const device = run.strategy === "mobile" ? "phone" : "computer";
    const tests = typeof run.samples === "number" && run.samples > 1
        ? `The middle of ${run.samples} tests Google ran on a simulated ${device}.`
        : typeof run.samples === "number"
            ? `1 test Google ran on a simulated ${device}.`
            : `A test Google ran on a simulated ${device}.`;
    return `${tests} Good for finding what to fix. The numbers move a little every run, so treat small changes as noise.`;
}

export function CoreWebVitalsCard({
    pageSpeed,
    domain,
    businessId,
    onRefreshData,
    strategy,
    onStrategyChange,
}: {
    pageSpeed: PageSpeed;
    domain: string;
    /** Live, writable business id: enables the on-demand Re-scan (#3401). */
    businessId?: string;
    /** Re-fetch the business detail after a successful re-scan; the refreshed
     *  pageSpeed block rides on that payload, not on the sync response. */
    onRefreshData?: () => void;
    /** Held by the screen, so the phone tile can open the card on Mobile. */
    strategy: PageSpeedRun["strategy"];
    onStrategyChange: (s: PageSpeedRun["strategy"]) => void;
}) {
    const snackbar = useSnackbar();
    const [rescanning, setRescanning] = useState(false);
    // The runs as they stood when Re-scan was pressed, so the refreshed card can
    // show what MOVED rather than just a new set of numbers. Kept in state (not a
    // ref) because the comparison has to survive the re-render that the refreshed
    // payload triggers. Null until the first re-scan of this session.
    const [baseline, setBaseline] = useState<PageSpeedRun[] | null>(null);

    // STRICT: the run for the selected strategy, or nothing.
    //
    // This used to fall back to `?? pageSpeed.runs[0]`. A PSI call can fail per
    // strategy (quota, timeout: `_psi_call` drops a failure silently), so a
    // block holding only the mobile run rendered MOBILE numbers under a
    // "Desktop" toggle with nothing to say so.
    const r = pageSpeed.runs.find((x) => x.strategy === strategy);
    const was = baseline?.find((x) => x.strategy === strategy);

    // Fresh scores without the paid full re-scan (#3401 / ourteam-backend#4352).
    // On failure the captured numbers stay exactly as they are: an error toast
    // over stale-but-real data beats a blanked card every time.
    const rescan = async () => {
        if (!businessId) return;
        setRescanning(true);
        setBaseline(pageSpeed.runs);
        const result = await syncPageSpeed(businessId);
        setRescanning(false);
        if (!result.ok) {
            setBaseline(null); // nothing moved, so nothing to compare against
            snackbar.showError("Could not re-run PageSpeed", result.message);
            return;
        }
        onRefreshData?.();
        // A 200 is not the same as "both strategies refreshed". The BE names the
        // strategies whose PSI call failed and keeps their LAST GOOD run while
        // moving `capturedAt` to now, so staying silent here put a stale run
        // under a fresh timestamp and called it complete.
        if (result.failedStrategies.length) {
            const failed = result.failedStrategies.join(" and ");
            snackbar.showError(
                `PageSpeed could not refresh ${failed}`,
                `The other strategy has fresh scores. The ${failed} numbers below are still from the previous run.`,
            );
            return;
        }
        snackbar.showSuccess("PageSpeed re-run complete", "Fresh scores are loading in.");
    };

    const canRescan = Boolean(businessId && onRefreshData);
    const RescanIcon = rescanning ? Loader2Icon : RefreshCwIcon;
    const rescanLink = canRescan ? (
        <button type="button" className={TEXT_ACTION} disabled={rescanning} onClick={() => void rescan()}>
            <RescanIcon aria-hidden size={12} className={cn(rescanning && "animate-spin")} />
            {rescanning ? "Re-scanning…" : "Re-scan"}
        </button>
    ) : null;
    /** In an empty body the Re-scan IS the thing to do, so it is a real button. */
    const rescanButton = canRescan ? (
        <PillButton size="sm" icon={RefreshCwIcon} busy={rescanning} onClick={() => void rescan()}>
            {rescanning ? "Re-scanning…" : "Re-scan"}
        </PillButton>
    ) : undefined;

    // scroll-mt: a tile that scrolls here leaves the card a little air above it.
    const head = { id: "cwv", alwaysOpen: true as const, title: "Core Web Vitals", icon: { icon: GaugeIcon, area: "site" as const }, anchor: "core-web-vitals", className: "scroll-mt-6" };

    // NO run at all, for either strategy: the BE omits the block rather than
    // fabricate one (run_pagespeed() in app/dependencies/aeo_pagespeed.py).
    //
    // Deliberately `runs.length`, not `!r`: "we have never measured this site"
    // and "the desktop call failed on a site we have measured" are different
    // findings. The second keeps the switch and the Re-scan so the reader can
    // switch back.
    if (pageSpeed.runs.length === 0) {
        return (
            <FoldCard {...head} right="PageSpeed Insights">
                <EmptyBody title="No PageSpeed data yet" action={rescanButton}>
                    We haven&apos;t captured a successful PageSpeed Insights run for this site yet.
                </EmptyBody>
            </FoldCard>
        );
    }

    return (
        <FoldCard
            {...head}
            right="PageSpeed Insights"
        >
            {/* Provenance and the control that refreshes it on the left, the
                switch that changes WHAT you are looking at on the right. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className={cn(TYPE.tm, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500")}>
                    {/* The RUN's own stamp (ourteam-backend#4470), falling back
                        to the block's on a payload stored before it. A refresh
                        where one strategy's PSI call fails keeps that
                        strategy's last good run: under the block stamp alone, a
                        week-old desktop run was dated "just now". */}
                    <Tooltip content={`When PageSpeed Insights last measured the ${strategy} run. If a re-scan fails for one strategy, that strategy keeps its previous numbers and the date they were taken.`}>
                        <span className="cursor-help whitespace-nowrap">
                            Last run {discoveredDateTime(r?.capturedAt || pageSpeed.capturedAt)}
                        </span>
                    </Tooltip>
                    {rescanLink && (
                        <>
                            <span aria-hidden className="text-gray-300">·</span>
                            {rescanLink}
                        </>
                    )}
                    <span aria-hidden className="text-gray-300">·</span>
                    {/* Straight from Google's public PSI API, so link the same
                        report: a score the reader can re-run themselves is worth
                        more than one they have to trust. `form_factor` matches
                        the switch, so the report that opens is the one being
                        compared against. */}
                    <a
                        href={`https://pagespeed.web.dev/analysis?url=${encodeURIComponent(pageHref(domain) ?? domain)}&form_factor=${strategy}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={TEXT_ACTION}
                    >
                        Verify on PageSpeed
                        <ExternalLinkIcon aria-hidden size={12} />
                    </a>
                </p>
                <Seg
                    size="sm"
                    ariaLabel="Device"
                    // Desktop first because it is the tab that opens (pageSpeed.ts).
                    // A switch whose selected item is not the first one it lists
                    // reads as though something moved it.
                    items={[
                        { key: "desktop", label: "Desktop" },
                        { key: "mobile", label: "Mobile" },
                    ]}
                    value={strategy}
                    onChange={onStrategyChange}
                />
            </div>

            {!r ? (
                /* This strategy's PSI call failed while the other succeeded.
                   The switch and Re-scan stay above so the reader can switch
                   back, and this is precisely where the old `?? runs[0]`
                   fallback used to render the OTHER strategy's numbers. */
                <EmptyBody title={`No ${strategy} run captured`} action={rescanButton}>
                    PageSpeed Insights returned a {strategy === "mobile" ? "desktop" : "mobile"} result for this site but not a {strategy} one.
                    Switch device above, or re-scan to try again.
                </EmptyBody>
            ) : (
                <>
                    <div className="mt-5 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
                        <div>
                            <div className="grid max-w-[520px] grid-cols-2 gap-y-4 sm:grid-cols-4">
                                <ScoreGauge
                                    score={r.performance}
                                    label="Performance"
                                    delta={deltaOf(r.performance, was?.performance, (n) => `${n}`, false)}
                                />
                                <ScoreGauge score={r.accessibility ?? null} label="Accessibility" />
                                <ScoreGauge score={r.bestPractices ?? null} label="Best Practices" />
                                <ScoreGauge score={r.seo ?? null} label="SEO" />
                            </div>
                            {/* FIELD first, because pagespeed.web.dev leads its
                                report with exactly this section. The most common
                                cause of "Oura's numbers are wrong" was reading
                                PSI's field LCP against Oura's lab LCP. */}
                            {/* The block reads the SELECTED device's run, so it names
                                that device: a phone pass beside a desktop fail (and
                                the reverse) is not a contradiction once it says so. */}
                            <BlockHead title={`Real visitors, ${r.strategy === "mobile" ? "Mobile" : "Desktop"}`} className="mt-6">
                                What people using Chrome went through on this site over the last 28 days. Google judges the site on this.
                            </BlockHead>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                <VerdictTile run={r} />
                                <MetricTile m={fieldInp(r)} delta={deltaOf(r.inpMs, was?.inpMs, (n) => `${n} ms`)} />
                            </div>
                        </div>
                        <DevicePreview run={r} />
                    </div>

                    <BlockHead title="Lab test" className="mt-5">{labLine(r)}</BlockHead>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {labMetrics(r).map((m) => (
                            <MetricTile key={m.label} m={m} delta={labDelta(m.label, r, was)} />
                        ))}
                    </div>

                    <div className="mt-5 grid gap-6 lg:grid-cols-2">
                        <div className="min-w-0">
                            <BlockHead title="Top opportunities">
                                The biggest wins on the homepage. Changes with device and every re-scan.
                                {typeof r.opportunitiesTotal === "number" && r.opportunitiesTotal > r.opportunities.length
                                    ? ` The top ${r.opportunities.length} of ${r.opportunitiesTotal}.`
                                    : ""}
                            </BlockHead>
                            <div className="mt-1">
                                {r.opportunities.length === 0 ? (
                                    <p className={cn(TYPE.tc, "m-0 py-3 text-gray-400")}>No opportunities on this run.</p>
                                ) : (
                                    r.opportunities.map((o) => (
                                        <ListRow
                                            key={o.id ?? o.label}
                                            // Wraps rather than truncates: the title IS the
                                            // finding, and a phone cut it mid-word.
                                            title={<span className="whitespace-normal">{o.label}</span>}
                                            sub={opportunityHelp(o)}
                                            right={
                                                <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap font-semibold tabular-nums text-amber-700")}>
                                                    {secs(o.savingMs)} faster
                                                </span>
                                            }
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                        <div className="min-w-0">
                            {/* Two corrections live in this line. These rows are
                                MOBILE whatever the switch says: run_pagespeed()
                                tests key pages on mobile only, hard-coded. And
                                Re-scan does not refresh them (refresh_pagespeed()
                                carries `pages` over unchanged), so since
                                ourteam-backend#4470 they carry their OWN stamp and
                                the card states it; older payloads have none. */}
                            <BlockHead title="Slowest key pages">
                                Your other important pages, not the homepage. Always tested on a phone, and only refreshed by a full scan.
                                {typeof pageSpeed.pagesTotal === "number" && pageSpeed.pagesTotal > pageSpeed.pages.length
                                    ? ` ${pageSpeed.pages.length} of ${pageSpeed.pagesTotal} tested.`
                                    : ""}
                                {pageSpeed.pagesCapturedAt ? ` Last measured ${discoveredDateTime(pageSpeed.pagesCapturedAt)}.` : ""}
                            </BlockHead>
                            <div className="mt-1">
                                {pageSpeed.pages.length === 0 ? (
                                    <p className={cn(TYPE.tc, "m-0 py-3 text-gray-400")}>No key pages were tested on this scan.</p>
                                ) : (
                                    slowestFirst(pageSpeed.pages).map((p) => {
                                        // Both nullable since ourteam-backend#4470.
                                        // Rendered unguarded, `secs(null)` printed
                                        // "LCP 0.0 s", a measurement nobody took.
                                        const score = p.performance;
                                        return (
                                            <ListRow
                                                key={p.url}
                                                title={p.title}
                                                sub={<span className="block truncate" title={p.url}>{pagePath(p.url)}</span>}
                                                right={
                                                    <>
                                                        {!notMeasured(p.lcpMs) && (
                                                            <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap tabular-nums text-gray-500")}>
                                                                LCP {secs(p.lcpMs)}
                                                            </span>
                                                        )}
                                                        {notMeasured(score) ? (
                                                            <span className={cn(TYPE.tm, "shrink-0 whitespace-nowrap text-gray-400")}>Not measured</span>
                                                        ) : (
                                                            <span className={cn(TYPE.tb, "w-8 shrink-0 text-right font-semibold tabular-nums", psiBand(score).text)}>
                                                                {score}
                                                            </span>
                                                        )}
                                                    </>
                                                }
                                            />
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </FoldCard>
    );
}
